import { BeneficiaryFilterNumberType } from '@repo/shared/dtos/beneficiary'
import { Button } from '@repo/ui/button'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/select'
import { useState } from 'react'

import { FilterFormProps } from './types'

export default function NumberFilterForm(props: FilterFormProps<BeneficiaryFilterNumberType>) {
  const { filter, onChange, onCancel } = props

  function buildForm(form: BeneficiaryFilterNumberType) {
    const baseForm: Partial<BeneficiaryFilterNumberType> = { type: form.type, key: form.key }
    switch (form.operator) {
      case 'is_null':
      case 'is_not_null':
        return { ...baseForm, operator: form.operator } as BeneficiaryFilterNumberType
      case 'eq':
      case 'neq':
      case 'gt':
      case 'lt':
        return {
          ...baseForm,
          operator: form.operator,
          value: form.value,
        } as BeneficiaryFilterNumberType
      case 'between':
        // Swap values if needed
        if (form.value > form.otherValue) {
          return {
            ...baseForm,
            operator: form.operator,
            value: form.otherValue,
            otherValue: form.value,
          } as BeneficiaryFilterNumberType
        } else {
          return {
            ...baseForm,
            operator: form.operator,
            value: form.value,
            otherValue: form.otherValue,
          } as BeneficiaryFilterNumberType
        }

      default:
        throw new Error('Unsupported operator')
    }
  }

  const [form, setForm] = useState<BeneficiaryFilterNumberType>(buildForm(filter))
  const { t } = useT({ keyPrefix: 'beneficiary.filters' })

  const makeNumber = (value: string) => {
    if (Number.isNaN(Number(value))) return 0
    return Number(value)
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    event.stopPropagation()

    onChange(buildForm(form))
    onCancel()
  }

  const handleOperatorChange = (operator: BeneficiaryFilterNumberType['operator']) => {
    const newForm = buildForm({ ...form, operator } as BeneficiaryFilterNumberType)
    setForm(newForm)
  }

  return (
    <form
      onSubmit={(event) => {
        handleSubmit(event)
      }}
      className="min-w-44 space-y-6"
    >
      <div className="space-y-4">
        <Select value={form.operator} onValueChange={handleOperatorChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select an operator" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="is_not_null">{t('operators.is_not_null')}</SelectItem>
            <SelectItem value="is_null">{t('operators.is_null')}</SelectItem>
            <SelectItem value="eq">{t('operators.eq')}</SelectItem>
            <SelectItem value="neq">{t('operators.neq')}</SelectItem>
            <SelectItem value="gt">{t('operators.gt')}</SelectItem>
            <SelectItem value="lt">{t('operators.lt')}</SelectItem>
            <SelectItem value="between">{t('operators.between')}</SelectItem>
          </SelectContent>
        </Select>

        {['eq', 'neq', 'gt', 'lt'].includes(form.operator) && (
          <Input
            type="number"
            value={'value' in form ? form.value : 0}
            onChange={(e) =>
              setForm({ ...form, value: makeNumber(e.target.value) } as BeneficiaryFilterNumberType)
            }
          />
        )}

        {form.operator === 'between' && (
          <div className="flex w-[15rem] items-center gap-2">
            <Input
              type="number"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: makeNumber(e.target.value) })}
            />
            <span className="text-primary-500 text-sm">{t('operators.and')}</span>
            <Input
              type="number"
              value={form.otherValue ?? 0}
              onChange={(e) => setForm({ ...form, otherValue: makeNumber(e.target.value) })}
            />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" onClick={onCancel} variant="ghost">
          {t('form.cancelButton')}
        </Button>
        <Button type="submit">{t('form.applyButton')}</Button>
      </div>
    </form>
  )
}
